import { Box, Flex, NumberFormatter } from "@mantine/core";
import type { IOtherAttributeConfigApi } from "@schemas/otherAttributesSchema";
import { ColDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import * as R from "ramda";
import React from "react";
import SimpleStampCheck from "../SimpleStampCheck/SimpleStampCheck";
import { CellEditFormatter } from "../columnGlobal";
import { formatNumberNb } from "../../app/utils";
import { formatShortDate } from "../../app/routes/admin/selectors";
import OtherAttributeDropdownEditor from "./OtherAttributeDropdownEditor";
const dateExportFormatter = (params: ValueFormatterParams) => {
  if (params.value == null || params.value === "") return "";
  const date = new Date(params.value);
  return Number.isNaN(date.getTime()) ? "" : formatShortDate(date, true);
};
export interface OtherAttributeColumnContext {
  handleUpdateField: (id: string, field: string, value: any, oldValue?: any) => Promise<void>;
  hideAndSort: (colId: string) => Partial<ColDef>;
  isOtherAttributeCellUnlocked: (id: string, field: string) => boolean;
  lockOtherAttributeCell: (id: string, field: string) => void;
  unlockOtherAttributeCell: (id: string, field: string) => void;
}

const isLockedAttributeCellEditable = (
  params: { data?: { _id?: string } | null; colDef?: { field?: string } },
  columnContext: OtherAttributeColumnContext,
  isLocked?: boolean,
) => {
  if (isLocked !== true) {
    return true;
  }

  const issueId = params.data?._id;
  const field = params.colDef?.field;
  if (issueId == null || field == null) {
    return false;
  }

  return columnContext.isOtherAttributeCellUnlocked(issueId, field);
};

const LockedOtherAttributeCell: React.FC<{
  children: React.ReactNode;
  columnContext: OtherAttributeColumnContext;
  isLocked?: boolean;
  params: ICellRendererParams;
}> = ({ children, columnContext, isLocked, params }) => {
  const issueId = params.data?._id;
  const field = params.colDef?.field;
  const isUnlocked =
    isLocked !== true || issueId == null || field == null
      ? true
      : columnContext.isOtherAttributeCellUnlocked(issueId, field);

  const refreshCurrentCell = () => {
    window.setTimeout(() => {
      params.api.refreshCells({
        columns: field != null ? [field] : undefined,
        force: true,
        rowNodes: [params.node],
      });
    }, 0);
  };

  const toggleLockState = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (issueId == null || field == null) {
      return;
    }

    if (isUnlocked) {
      columnContext.lockOtherAttributeCell(issueId, field);
      refreshCurrentCell();
      return;
    }

    columnContext.unlockOtherAttributeCell(issueId, field);
    refreshCurrentCell();

    if (params.node.rowIndex == null) {
      return;
    }

    window.setTimeout(() => {
      params.api.startEditingCell({
        rowIndex: params.node.rowIndex!,
        colKey: field,
      });
    }, 0);
  };

  return (
    <Flex align="center" gap={6} h="100%" w="100%">
      {isLocked === true && (
        <button
          type="button"
          title={isUnlocked ? "Lås feltet igjen" : "Lås opp for redigering"}
          aria-label={isUnlocked ? "Lås feltet igjen" : "Lås opp for redigering"}
          onClick={toggleLockState}
          className="border-0 bg-transparent"
          style={{
            color: isUnlocked ? "var(--mantine-color-green-7)" : "var(--mantine-color-gray-6)",
            cursor: "pointer",
            lineHeight: 1,
            padding: 0,
          }}
        >
          <i className={`fa fa-fw ${isUnlocked ? "fa-unlock" : "fa-lock"}`} />
        </button>
      )}
      <Box style={{ flexGrow: 1, overflow: "hidden" }}>{children}</Box>
    </Flex>
  );
};

export const getAgGridOtherAttributeColumns = ({
  columnContext,
  isTaskColumns,
  otherAttributesConfig,
}: {
  columnContext: OtherAttributeColumnContext;
  isTaskColumns?: boolean;
  otherAttributesConfig: IOtherAttributeConfigApi;
}): ColDef<any>[] => {
  const otherAttributeColumns: ColDef<any>[] = [];
  const otherAttributesEntries = Object.entries(otherAttributesConfig ?? {}).map(([key, value]) => ({
    key,
    ...value,
  }));

  const groupedBySection = R.groupBy((item) => item.section, otherAttributesEntries);
  Object.entries(groupedBySection).map(([sectionTitle, attributesInSection]) => {
    attributesInSection
      .sort((a, b) => a.order - b.order)
      .forEach((attribute) => {
        if (attribute.isActive !== true) {
          return;
        }

        const editableWhenUnlocked = (params) =>
          isLockedAttributeCellEditable(params, columnContext, attribute.locked === true);
        const renderLockedCell = (params: ICellRendererParams, content: React.ReactNode) => (
          <LockedOtherAttributeCell columnContext={columnContext} isLocked={attribute.locked === true} params={params}>
            {content}
          </LockedOtherAttributeCell>
        );
        let customParams: {};
        switch (attribute.typeOfValue) {
          case "MULTILINESTRING":
            customParams = {
              filter: "agTextColumnFilter",
              cellEditor: "agLargeTextCellEditor",
              cellEditorPopup: true,
              minWidth: 200,
              cellRenderer: (params) => renderLockedCell(params, CellEditFormatter(params.value)),
              editable: editableWhenUnlocked,
              autoHeight: true,
            };
            break;
          case "NUMBER":
            customParams = {
              filter: "agNumberColumnFilter",
              cellEditor: "agNumberCellEditor",
              valueGetter: (params) => {
                const val = params.data.otherAttributesData?.[attribute.key];
                if (Array.isArray(val) || val == null || val === "") return null;
                const num = Number(val);
                return Number.isNaN(num) ? null : num;
              },
              valueFormatter: (params) => formatNumberNb(params.value),
              valueSetter: (params) => {
                const newValue = params.newValue;
                if (Array.isArray(newValue)) return false;

                const cleaned = String(newValue)
                  .replace(/[\s\u00A0\u202F]/g, "")
                  .replace(",", ".");
                const num = Number(cleaned);

                if (Number.isNaN(num)) return false;

                params.data.otherAttributesData = {
                  ...params.data.otherAttributesData,
                  [attribute.key]: num,
                };
                return true;
              },
              editable: editableWhenUnlocked,
              cellRenderer: (params) => renderLockedCell(params, formatNumberNb(params.value)),
            };
            break;
          case "BOOLEAN":
            customParams = {
              filter: "agFilter",
              editable: false,
              valueGetter: (params) =>
                params.data.otherAttributesData?.[attribute.key] === true
                  ? "Ja"
                  : params.data.otherAttributesData?.[attribute.key] === false
                  ? "Nei"
                  : "",
              cellRenderer: (params) => (
                <LockedOtherAttributeCell
                  columnContext={columnContext}
                  isLocked={attribute.locked === true}
                  params={params}
                >
                  <SimpleStampCheck
                    disabled={!isLockedAttributeCellEditable(params, columnContext, attribute.locked === true)}
                    toggled={params.value == "" ? null : params.value === "Ja"}
                    toggle={async (val) => {
                      await columnContext.handleUpdateField(params.data._id, params.colDef.field, val);
                      if (attribute.locked === true) {
                        columnContext.lockOtherAttributeCell(params.data._id, params.colDef.field);
                      }
                    }}
                  />
                </LockedOtherAttributeCell>
              ),
            };
            break;
          case "DROPDOWN":
            const options = otherAttributesConfig?.[attribute.key]?.tableOptions ?? [];
            const multiSelect = attribute.allowMultipleSelectionsInDropdown === true;
            const isNumberDropdown = otherAttributesConfig?.[attribute.key]?.dropdownOptionType === "NUMBER";
            const showDropdownOptionDescriptions =
              otherAttributesConfig?.[attribute.key]?.showDropdownOptionDescriptions === true;
            const OptionWithColor = (params: ValueFormatterParams & { showDescription?: boolean }) => {
              if (params.value == null) {
                return null;
              }
              const values = Array.isArray(params.value) ? params.value : [params.value];

              return (
                <Flex wrap="wrap">
                  {values.map((value, index) => {
                    const foundOption = options.find((option) => String(option.value) === String(value));
                    const showDescription =
                      params.showDescription === true &&
                      foundOption?.description != null &&
                      foundOption.description.trim() !== "";
                    return (
                      <Flex key={index} align="center" mr="0.5rem">
                        {foundOption?.color && (
                          <div className="statusdot mt-1" style={{ backgroundColor: foundOption?.color }} />
                        )}
                        <div>
                          <div>
                            {isNumberDropdown && Number.isFinite(Number(value)) ? (
                              <NumberFormatter value={Number(value)} thousandSeparator=" " decimalSeparator="," />
                            ) : (
                              String(value ?? "")
                            )}
                          </div>
                          {showDescription && (
                            <div
                              className="text-muted"
                              style={{
                                fontSize: "0.8rem",
                                lineHeight: 1.25,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {foundOption.description}
                            </div>
                          )}
                        </div>
                      </Flex>
                    );
                  })}
                </Flex>
              );
            };
            const formatDropdownValue = (value: unknown) => {
              if (Array.isArray(value)) {
                return value.map((item) => (isNumberDropdown ? formatNumberNb(item) : String(item ?? ""))).join(", ");
              }

              return isNumberDropdown ? formatNumberNb(value) : String(value ?? "");
            };
            customParams = {
              filter: "agFilter",
              cellEditor: OtherAttributeDropdownEditor,
              cellEditorPopup: true,
              cellEditorPopupPosition: "under",
              editable: editableWhenUnlocked,
              valueFormatter: (params) => formatDropdownValue(params.value),
              valueSetter: (params) => params.newValue,
              cellEditorParams: {
                options,
                formatOption: formatDropdownValue,
                showDescriptions: showDropdownOptionDescriptions,
                multiSelect,
              },
              cellRenderer: (params) =>
                renderLockedCell(params, <OptionWithColor {...params} showDescription={false} />),
            };
            break;
          case "DATE":
            customParams = {
              filter: "agDateColumnFilter",
              cellEditor: "agDateStringCellEditor",
              valueFormatter: dateExportFormatter,
              valueGetter: (params) => params.data.otherAttributesData?.[attribute?.key] ?? "",
              editable: editableWhenUnlocked,
              cellRenderer: (params) => renderLockedCell(params, dateExportFormatter(params)),
            };
            break;
          case "FORMULA":
            customParams = {
              editable: false,
              filter: "agTextColumnFilter",
              minWidth: 200,
            };
            break;
          case "MATH_FORMULA":
            customParams = {
              editable: false,
              filter: "agTextColumnFilter",
              minWidth: 200,
              valueFormatter: (params) => formatNumberNb(params.value),
            };
            break;
          default:
            customParams = {
              filter: "agTextColumnFilter",
              editable: editableWhenUnlocked,
              cellRenderer: (params) => renderLockedCell(params, CellEditFormatter(params.value)),
            };
            break;
        }

        const otherAttributeColumn: ColDef = {
          field: `otherAttributesData.${attribute.key}`,
          headerName: attribute.label,
          autoHeaderHeight: true,
          headerComponentParams: {
            template: customAttributeHeaderTemplate(
              attribute.labelColor,
              attribute.locked,
              attribute.typeOfValue === "FORMULA" || attribute.typeOfValue === "MATH_FORMULA",
              attribute.typeOfValue === "MATH_FORMULA",
            ),
          },
          editable: editableWhenUnlocked,
          ...customParams,
          maxWidth: 300,
          context: sectionTitle,
          cellClass: "cell-overflow-hidden cell-px1",
          ...(isTaskColumns !== true ? columnContext.hideAndSort(`otherAttributesData.${attribute.key}`) : {}),
        };
        otherAttributeColumns.push(otherAttributeColumn);
      });
  });

  return otherAttributeColumns;
};

const customAttributeHeaderTemplate = (
  color: string,
  isLocked: boolean,
  isFormula: boolean,
  isMathFormula: boolean,
) => `<div class="ag-cell-label-container py-0" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
          <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
          <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
          <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
          <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
          ${
            isLocked === true
              ? `<span title="Feltet er låst. Lås opp i cellen før redigering." style="margin-right: 0.25rem; cursor: default;" onclick="event.stopPropagation(); event.preventDefault();" onmousedown="event.stopPropagation();"><i class="fa fa-lock"></i></span>`
              : ""
          }
          ${
            color != null && color.length > 0
              ? `<div class="mmi-dot" style="margin-right: 0.25rem; background-color: ${color};"></div>`
              : ""
          }
          ${
            isFormula
              ? `<div class="text-muted" title="Formelfelt" style="margin-right: 0.25rem; font-weight: 600; font-size: 12px;">Æ’x</div>`
              : ""
          }
          ${
            isMathFormula
              ? `<div title="Beta-funksjon" style="margin-right: 0.25rem; font-weight: 700; font-size: 10px; color: #8a6d3b; background: #fcf8e3; border: 1px solid #faebcc; border-radius: 10px; padding: 0 6px; line-height: 16px;">BETA</div>`
              : ""
          }
          <span data-ref="eText" class="ag-header-cell-text three-line-text-truncate" role="columnheader"></span>
          <div style="display: flex; align-items: baseline; flex-direction: row;"></div>
          <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
        </div>
      </div>`;
